import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import Papa from "papaparse";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import config from "../Config/config";
import ToastAlert, { showToast } from "../Common/ToastAlert";

const primaryColor = "#333999";
const CONTACT_IMPORT_FIELD_DEFS = [
  {
    dbField: "First Name",
    required: false,
    aliases: ["firstname", "first name", "first_name", "first-name", "fname", "first"],
  },
  {
    dbField: "Last Name",
    required: false,
    aliases: ["lastname", "last name", "last_name", "last-name", "lname", "surname", "last"],
  },
  {
    dbField: "Email",
    required: true,
    aliases: ["email", "emailaddress", "email address", "e-mail", "mail"],
  },
  {
    dbField: "Phone",
    required: false,
    aliases: [
      "phone",
      "phonenumber",
      "phone number",
      "phone no",
      "pno",
      "mobile",
      "cell",
      "telephone",
      "tel",
      "contactnumber",
      "phone numeber",
      "phone numberr",
    ],
  },
  {
    dbField: "Address",
    required: false,
    aliases: [
      "address",
      "location",
      "street",
      "streetaddress",
      "mailingaddress",
      "addr",
      "citystate",
    ],
  },
  {
    dbField: "Company",
    required: false,
    aliases: ["company", "companyname", "business", "organization", "organisation", "org", "firm"],
  },
];

const normalizeCsvHeaderKey = (value = "") => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const makeEmptyFieldMapping = () =>
  CONTACT_IMPORT_FIELD_DEFS.reduce((acc, def) => {
    acc[def.dbField] = "";
    return acc;
  }, {});

const autoMapCsvHeadersToDbFields = (headers = []) => {
  const cleanedHeaders = Array.isArray(headers) ? headers.map((h) => String(h || "").trim()).filter(Boolean) : [];
  const candidates = cleanedHeaders.map((header) => ({
    header,
    norm: normalizeCsvHeaderKey(header),
  }));
  const used = new Set();
  const mapping = makeEmptyFieldMapping();

  const tryMatch = (fieldDef) => {
    const aliasNorms = (fieldDef.aliases || []).map((a) => normalizeCsvHeaderKey(a)).filter(Boolean);
    if (!aliasNorms.length) return "";

    let hit = candidates.find((c) => !used.has(c.header) && aliasNorms.includes(c.norm));
    if (!hit) {
      hit = candidates.find(
        (c) =>
          !used.has(c.header) &&
          aliasNorms.some((alias) => c.norm.includes(alias) || alias.includes(c.norm))
      );
    }
    if (!hit) return "";
    used.add(hit.header);
    return hit.header;
  };

  CONTACT_IMPORT_FIELD_DEFS.forEach((def) => {
    mapping[def.dbField] = tryMatch(def);
  });

  return mapping;
};

const isSpreadsheetFile = (file) => {
  const name = String(file?.name || "").toLowerCase();
  const type = String(file?.type || "").toLowerCase();
  return (
    name.endsWith(".xlsx") ||
    name.endsWith(".xls") ||
    type.includes("spreadsheetml") ||
    type.includes("ms-excel") ||
    type.includes("sheet")
  );
};

const isCsvFile = (file) => {
  const name = String(file?.name || "").toLowerCase();
  const type = String(file?.type || "").toLowerCase();
  return name.endsWith(".csv") || name.endsWith(".tsv") || type.includes("csv") || type === "text/plain";
};

const normalizeHeaderRow = (headerRow = []) =>
  (Array.isArray(headerRow) ? headerRow : []).map((h, idx) => {
    const value = String(h ?? "").replace(/\u00a0/g, " ").trim();
    return value || `Column ${idx + 1}`;
  });

const buildRowsFromMatrix = (matrix = [], headerRowIdx = 0) => {
  if (!Array.isArray(matrix) || !matrix.length) return { rows: [], headers: [] };
  if (headerRowIdx < 0 || headerRowIdx >= matrix.length) return { rows: [], headers: [] };

  const headerRow = Array.isArray(matrix[headerRowIdx]) ? matrix[headerRowIdx] : [];
  const headers = normalizeHeaderRow(headerRow);
  if (!headers.length) return { rows: [], headers: [] };

  const rows = [];
  for (let r = headerRowIdx + 1; r < matrix.length; r += 1) {
    const src = Array.isArray(matrix[r]) ? matrix[r] : [];
    const isEmpty = src.every((cell) => String(cell ?? "").trim() === "");
    if (isEmpty) continue;
    const out = {};
    for (let c = 0; c < headers.length; c += 1) out[headers[c]] = src[c] ?? "";
    rows.push(out);
  }

  return { rows, headers };
};

const parseCsvRowsFromFile = (file, { watchersFile = false } = {}) =>
  new Promise((resolve, reject) => {
    if (!watchersFile) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const rows = Array.isArray(results?.data) ? results.data : [];
          const headers = Array.isArray(results?.meta?.fields) ? results.meta.fields : [];
          resolve({ rows, headers });
        },
        error: reject,
      });
      return;
    }

    Papa.parse(file, {
      header: false,
      skipEmptyLines: false,
      complete: (results) => {
        const matrix = Array.isArray(results?.data) ? results.data : [];
        resolve(buildRowsFromMatrix(matrix, 1));
      },
      error: reject,
    });
  });

const parseSpreadsheetRowsFromFile = async (file, { watchersFile = false } = {}) => {
  const xlsxModule = await import("xlsx");
  const XLSXLib = xlsxModule?.default && xlsxModule.default.read ? xlsxModule.default : xlsxModule;
  const data = await file.arrayBuffer();
  const wb = XLSXLib.read(data, { type: "array", cellDates: true });
  const sheetName = wb.SheetNames?.[0];
  if (!sheetName) return { rows: [], headers: [] };
  const ws = wb.Sheets[sheetName];
  if (!ws) return { rows: [], headers: [] };

  const matrix = XLSXLib.utils.sheet_to_json(ws, { header: 1, defval: "" });
  if (!Array.isArray(matrix) || !matrix.length) return { rows: [], headers: [] };

  if (watchersFile) {
    return buildRowsFromMatrix(matrix, 1);
  }

  const headerRowIdx = matrix.findIndex((row) => Array.isArray(row) && row.some((cell) => String(cell ?? "").trim() !== ""));
  if (headerRowIdx < 0) return { rows: [], headers: [] };
  return buildRowsFromMatrix(matrix, headerRowIdx);
};

// ---- Contacts list cache helpers (shared with MarketingEmail) ----
// We cache per category + emailOnly filter. Stored in sessionStorage.
const CONTACTS_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

const safeJsonParse = (s) => {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
};

const hasValidEmail = (c) => {
  const e = String(c?.email || c?.Email || "").trim();
  return /^\S+@\S+\.\S+$/.test(e);
};

const deriveNameParts = (raw) => {
  const firstFromField = String(raw?.firstName ?? raw?.["First Name"] ?? "").trim();
  const lastFromField = String(raw?.lastName ?? raw?.["Last Name"] ?? "").trim();
  const nameFromField = String(raw?.name ?? raw?.Name ?? "").trim();
  if (firstFromField || lastFromField) {
    return {
      firstName: firstFromField,
      lastName: lastFromField,
      name: String([firstFromField, lastFromField].filter(Boolean).join(" ")).trim() || nameFromField || "",
    };
  }

  if (!nameFromField) return { firstName: "", lastName: "", name: "" };

  const parts = nameFromField.split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
    name: nameFromField,
  };
};

const normalizeContact = (raw) => {
  // The contacts list page expects lowercase fields.
  const email = String(raw?.email ?? raw?.Email ?? "").trim().toLowerCase();
  const names = deriveNameParts(raw);
  return {
    ...raw,
    id: raw?.id || raw?._id || raw?.docId || raw?.Email || raw?.email,
    email,
    name: names.name,
    firstName: names.firstName,
    lastName: names.lastName,
    company: String(raw?.company ?? raw?.Company ?? "").trim(),
    phone: String(raw?.phone ?? raw?.Phone ?? "").trim(),
    address: String(raw?.address ?? raw?.Address ?? "").trim(),
    website: String(raw?.website ?? raw?.Website ?? "").trim(),
    emailSent: Boolean(raw?.emailSent ?? raw?.sent ?? false),
    notes: raw?.notes ?? raw?.note ?? "",
  };
};

const getContactsCacheKey = (categoryName, { emailOnly } = {}) => {
  const cat = String(categoryName || "").trim();
  return `contactsCache:v2:${cat}:emailOnly=${emailOnly ? 1 : 0}`;
};

const readContactsCache = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = safeJsonParse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    // Back-compat: older versions stored {contacts: []}
    const ts = Number(parsed.ts || 0);
    if (!ts || Date.now() - ts > CONTACTS_CACHE_TTL_MS) return null;

    if (Array.isArray(parsed.contacts)) {
      const normalized = parsed.contacts.map(normalizeContact);
      const noEmail = normalized.filter((c) => !hasValidEmail(c));
      const withEmail = normalized.filter((c) => hasValidEmail(c));
      const sent = withEmail.filter((c) => c.emailSent);
      const unsent = withEmail.filter((c) => !c.emailSent);
      return { ts, sent, unsent, noEmail };
    }

    const unsent = Array.isArray(parsed.unsent) ? parsed.unsent : [];
    const sent = Array.isArray(parsed.sent) ? parsed.sent : [];
    const noEmail = Array.isArray(parsed.noEmail) ? parsed.noEmail : [];
    return { ts, unsent, sent, noEmail };
  } catch {
    return null;
  }
};

const writeContactsCache = (key, payload) => {
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify({ ts: Date.now(), unsent: payload.unsent || [], sent: payload.sent || [], noEmail: payload.noEmail || [] })
    );
  } catch {
    // ignore
  }
};

const removeContactFromBuckets = (buckets, contactIdOrEmail) => {
  const idKey = String(contactIdOrEmail || "").trim().toLowerCase();
  const filterOut = (arr) => (Array.isArray(arr) ? arr.filter((c) => {
    const cid = String(c?.id || "").trim().toLowerCase();
    const em = String(c?.email || "").trim().toLowerCase();
    return cid !== idKey && em !== idKey;
  }) : []);
  return {
    unsent: filterOut(buckets?.unsent),
    sent: filterOut(buckets?.sent),
    noEmail: filterOut(buckets?.noEmail),
  };
};

const upsertContactIntoBuckets = (buckets, contact) => {
  const c = normalizeContact(contact);
  // Remove existing copy first (across all buckets)
  const removed = removeContactFromBuckets(buckets, c.id || c.email);

  if (!hasValidEmail(c)) {
    return { ...removed, noEmail: [c, ...(removed.noEmail || [])] };
  }
  if (c.emailSent) {
    return { ...removed, sent: [c, ...(removed.sent || [])] };
  }
  return { ...removed, unsent: [c, ...(removed.unsent || [])] };
};

const bumpContactsCategoryVersion = (categoryName) => {
  // Simple invalidation marker that other pages can watch.
  try {
    const key = `contactsCategoryVersion:${String(categoryName || "").trim()}`;
    const next = Date.now();
    localStorage.setItem(key, String(next));
  } catch {
    // ignore
  }
};

const MarketingContacts = ({ selectedCategory }) => {

  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [fieldMapping, setFieldMapping] = useState(makeEmptyFieldMapping);
  const [openCsvMappingDialog, setOpenCsvMappingDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [isWatchersFile, setIsWatchersFile] = useState(false);
  const uploadFileInputRef = useRef(null);

  // Add single contact (submit through CSV upload flow)
  const [openAddContactDialog, setOpenAddContactDialog] = useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const [singleContact, setSingleContact] = useState({
    "First Name": "",
    "Last Name": "",
    Email: "",
    Company: "",
    Phone: "",
    Address: "",
    Website: "",
  });
  // Derive selectedCategoryName from prop
  const selectedCategoryName = selectedCategory?.name || "";
  const orgHeaders = useMemo(() => {
    const headers = {};
    const userId = String(localStorage.getItem("userId") || "").trim();
    const subscriptionId = String(localStorage.getItem("subscriptionId") || "").trim();
    if (userId) headers["x-user-id"] = userId;
    if (subscriptionId) headers["x-subscription-id"] = subscriptionId;
    return headers;
  }, []);



  const handleOpenAddContact = () => {
    if (!selectedCategoryName) {
      showToast("Please select a category", "warning");
      return;
    }
    setSingleContact({
      "First Name": "",
      "Last Name": "",
      Email: "",
      Company: "",
      Phone: "",
      Address: "",
      Website: "",
    });
    setOpenAddContactDialog(true);
  };

  const handleCloseAddContact = () => {
    if (addingContact) return;
    setOpenAddContactDialog(false);
  };

  const updateSingleContact = (field, value) => {
    setSingleContact((prev) => ({ ...prev, [field]: value }));
  };

  const clearCsvUploadDraft = () => {
    setCsvFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setFieldMapping(makeEmptyFieldMapping());
    setOpenCsvMappingDialog(false);
    if (uploadFileInputRef.current) uploadFileInputRef.current.value = "";
  };

  const handleCancelCsvMapping = () => {
    if (uploading) return;
    clearCsvUploadDraft();
  };

  const handleFileChange = (e) => {
    if (!selectedCategoryName) {
      showToast("Please select a category", "warning");
      e.target.value = "";
      return;
    }

    const file = e.target.files?.[0];
    clearCsvUploadDraft();
    setCsvFile(file);
    setUploadResult(null);

    if (!file) return;

    if (!isSpreadsheetFile(file) && !isCsvFile(file)) {
      showToast("Upload CSV, XLSX, or XLS file.", "warning");
      clearCsvUploadDraft();
      return;
    }

    (async () => {
      try {
        const parsed = isSpreadsheetFile(file)
          ? await parseSpreadsheetRowsFromFile(file, { watchersFile: isWatchersFile })
          : await parseCsvRowsFromFile(file, { watchersFile: isWatchersFile });

        const rows = Array.isArray(parsed?.rows) ? parsed.rows : [];
        const headers = Array.isArray(parsed?.headers) ? parsed.headers : [];
        setCsvData(rows);
        setCsvHeaders(headers);
        setFieldMapping({ ...makeEmptyFieldMapping(), ...autoMapCsvHeadersToDbFields(headers) });
        if (!rows.length || !headers.length) {
          showToast(
            isWatchersFile
              ? "No usable rows found. For watchers files, row 1 is ignored and row 2 must contain headers."
              : "No usable rows found in file.",
            "warning"
          );
          return;
        }
        setOpenCsvMappingDialog(true);
      } catch {
        showToast(
          isWatchersFile
            ? "Failed to parse watcher file. Row 1 should be auction name and row 2 should be headers."
            : "Failed to parse file. Use CSV/XLSX with a header row.",
          "error"
        );
        clearCsvUploadDraft();
      }
    })();
  };

  const handleMappingChange = (dbField, mappedCsvHeader) => {
    setFieldMapping((prev) => {
      const next = { ...prev };
      if (mappedCsvHeader) {
        Object.keys(next).forEach((k) => {
          if (k !== dbField && next[k] === mappedCsvHeader) next[k] = "";
        });
      }
      next[dbField] = mappedCsvHeader;
      return next;
    });
  };

  const mappedFieldDefs = useMemo(
    () => CONTACT_IMPORT_FIELD_DEFS.filter((def) => String(fieldMapping[def.dbField] || "").trim()),
    [fieldMapping]
  );

  const handleUpload = async () => {
    if (!csvFile) return;
    if (!selectedCategoryName) {
      showToast("Please select a category", "warning");
      return;
    }
    const emailHeader = String(fieldMapping.Email || "").trim();
    if (!emailHeader) {
      showToast("Map the Email field before uploading.", "warning");
      return;
    }
    if (!mappedFieldDefs.length) {
      showToast("Map at least one CSV column before uploading.", "warning");
      return;
    }

    // Expansion of csvData for multiple emails per row
    let expandedRows = csvData;

    if (emailHeader) {
      expandedRows = [];
      csvData.forEach((row) => {
        const emails = (row[emailHeader] || "").split(",").map((e) => e.trim()).filter((e) => e);
        if (emails.length > 0) {
          emails.forEach((email) => {
            expandedRows.push({ ...row, [emailHeader]: email });
          });
        } else {
          expandedRows.push(row);
        }
      });
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      const mappingForApi = {};
      Object.entries(fieldMapping).forEach(([dbField, csvHeader]) => {
        const header = String(csvHeader || "").trim();
        if (header) mappingForApi[header] = dbField;
      });

      // Upload normalized CSV so backend ingestion stays consistent for CSV/XLSX inputs.
      const csvString = Papa.unparse(expandedRows);
      const csvBlob = new Blob([csvString], { type: "text/csv" });
      const baseName = String(csvFile?.name || "contacts")
        .replace(/\.[^/.]+$/, "")
        .trim();
      const uploadName = `${baseName || "contacts"}_mapped.csv`;

      formData.append("file", csvBlob, uploadName);
      formData.append("category", selectedCategoryName);
      formData.append("mapping", JSON.stringify(mappingForApi));

      const res = await axios.post(
        `${String(config.API_BASE_URL || "")}/upload-contacts`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...orgHeaders,
          },
        }
      );

      setUploadResult({
        success: res.data.success !== undefined ? res.data.success : true,
        message: res.data.message || "Upload complete",
        added: res.data.added,
        updatedExisting: res.data.updatedExisting,
        skippedAuth: res.data.skippedAuth,
        skippedDup: res.data.skippedDup,
        failed: res.data.failed,
      });

      // Update cache: since CSV can add many contacts, safest is to invalidate.
      const cacheKeyEmailOnly = getContactsCacheKey(selectedCategoryName, { emailOnly: true });
      const cacheKeyAll = getContactsCacheKey(selectedCategoryName, { emailOnly: false });
      try {
        sessionStorage.removeItem(cacheKeyEmailOnly);
        sessionStorage.removeItem(cacheKeyAll);
      } catch {
        // ignore
      }
      bumpContactsCategoryVersion(selectedCategoryName);

      clearCsvUploadDraft();
    } catch (error) {
      console.error("Error uploading contacts:", error);
      setUploadResult({ success: false, message: "Upload failed." });
    }

    setUploading(false);
  };

  const handleAddSingleContact = async () => {
    if (!selectedCategoryName) {
      showToast("Please select a category", "warning");
      return;
    }

    const email = String(singleContact.Email || "").trim();
    if (!email) {
      showToast("Email is required", "warning");
      return;
    }

    // Basic email sanity check (not perfect, but avoids obvious mistakes)
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      showToast("Please enter a valid email", "warning");
      return;
    }

    setAddingContact(true);
    setUploadResult(null);

    try {
      const firstName = String(singleContact["First Name"] || "").trim();
      const lastName = String(singleContact["Last Name"] || "").trim();
      const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

      // Build a one-row CSV with known headers
      const row = {
        "First Name": firstName,
        "Last Name": lastName,
        Name: fullName,
        Email: email,
        Company: String(singleContact.Company || "").trim(),
        Phone: String(singleContact.Phone || "").trim(),
        Address: String(singleContact.Address || "").trim(),
        Website: String(singleContact.Website || "").trim(),
      };

      const csvString = Papa.unparse([row], {
        columns: ["First Name", "Last Name", "Name", "Email", "Company", "Phone", "Address", "Website"],
      });
      const csvBlob = new Blob([csvString], { type: "text/csv" });

      // Mapping: CSV headers -> Firestore fields (same names)
      const mapping = {
        "First Name": "First Name",
        "Last Name": "Last Name",
        Name: "Name",
        Email: "Email",
        Company: "Company",
        Phone: "Phone",
        Address: "Address",
        Website: "Website",
      };

      const formData = new FormData();
      formData.append("file", csvBlob, `single-contact-${Date.now()}.csv`);
      formData.append("category", selectedCategoryName);
      formData.append("mapping", JSON.stringify(mapping));

      const base = String(config.API_BASE_URL || "").replace(/\/api\/?$/, "");
      const res = await axios.post(`${base}/upload-contacts`, formData, {
        headers: { "Content-Type": "multipart/form-data", ...orgHeaders },
      });

      setUploadResult({
        success: res.data.success !== undefined ? res.data.success : true,
        message: res.data.message || "Contact added",
        added: res.data.added,
        updatedExisting: res.data.updatedExisting,
        skippedAuth: res.data.skippedAuth,
        skippedDup: res.data.skippedDup,
        failed: res.data.failed,
      });

      // Patch cache with the new contact so the list page doesn't need to refetch.
      const cacheKeyEmailOnly = getContactsCacheKey(selectedCategoryName, { emailOnly: true });
      const existing = readContactsCache(cacheKeyEmailOnly) || { unsent: [], sent: [], noEmail: [] };
      const created = normalizeContact({
        id: email, // best-effort; backend may use docId but email is stable enough for cache
        email,
        firstName: row["First Name"],
        lastName: row["Last Name"],
        name: row.Name,
        company: row.Company,
        phone: row.Phone,
        address: row.Address,
        website: row.Website,
        emailSent: false,
      });
      const nextBuckets = upsertContactIntoBuckets(existing, created);
      writeContactsCache(cacheKeyEmailOnly, nextBuckets);

      // Also patch the non-emailOnly cache if it exists
      const cacheKeyAll = getContactsCacheKey(selectedCategoryName, { emailOnly: false });
      const existingAll = readContactsCache(cacheKeyAll);
      if (existingAll) {
        const nextAll = upsertContactIntoBuckets(existingAll, created);
        writeContactsCache(cacheKeyAll, nextAll);
      }

      bumpContactsCategoryVersion(selectedCategoryName);

      setOpenAddContactDialog(false);
      showToast("Contact submitted", "success");
    } catch (error) {
      console.error("Error adding single contact:", error);
      setUploadResult({ success: false, message: "Failed to add contact." });
      showToast("Failed to add contact", "error");
    } finally {
      setAddingContact(false);
    }
  };

  return (
    <div className="w-full">
      <div className="crd w-full">
        {/* Header */}
        <div className="flex flex-col items-center text-center gap-2">
          <h3 className="text-lg sm:text-xl font-extrabold text-[#333999]">
            Upload Marketing Contacts
          </h3>
          <div className="ln-gradient-divider max-w-[520px]" />

          {selectedCategoryName ? (
            <div className="text-sm text-slate-600">
              Category{" "}
              <span className="ln-badge ln-badge-primary ml-1">
                {selectedCategoryName}
              </span>
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              Select a category above to upload or add contacts.
            </div>
          )}
        </div>

        {/* Actions */}
        {selectedCategoryName && (
          <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            <label className="btn-primary btn-md cursor-pointer w-full sm:w-auto text-center">
              Upload CSV/XLSX File
              <input
                ref={uploadFileInputRef}
                type="file"
                accept=".csv,.tsv,text/csv,text/tab-separated-values,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                hidden
                onChange={handleFileChange}
              />
            </label>

            <button
              type="button"
              onClick={handleOpenAddContact}
              className="btn-ghost btn-md w-full sm:w-auto"
            >
              Add Single Contact
            </button>
          </div>
        )}
        {selectedCategoryName && (
          <div className="mt-3 flex justify-center">
            <label
              className={`inline-flex items-center gap-2 text-sm text-slate-700 ${
                openCsvMappingDialog || uploading ? "opacity-60" : ""
              }`}
            >
              <input
                type="checkbox"
                checked={isWatchersFile}
                onChange={(e) => setIsWatchersFile(e.target.checked)}
                disabled={openCsvMappingDialog || uploading}
                className="h-4 w-4 accent-[#333999]"
              />
              <span>Watchers file (ignore row 1 auction name; use row 2 as headers)</span>
            </label>
          </div>
        )}

        {/* Upload result */}
        {uploadResult && (
          <div
            className={`mt-5 rounded-xl border p-4 shadow-sm ${
              uploadResult.success
                ? "border-emerald-200 bg-emerald-50"
                : "border-red-200 bg-red-50"
            }`}
          >
            <div
              className="text-sm font-semibold"
              style={{
                color: uploadResult.success ? "#047857" : "#b91c1c",
              }}
            >
              {uploadResult.success
                ? `Upload successful: ${uploadResult.message || ""}`
                : `Upload failed: ${uploadResult.message || "Unknown error."}`}
            </div>

            {(uploadResult.added !== undefined ||
              uploadResult.updatedExisting !== undefined ||
              uploadResult.skippedAuth !== undefined ||
              uploadResult.skippedDup !== undefined ||
              uploadResult.failed !== undefined) && (
              <div className="mt-1 text-xs text-slate-600">
                Added: {uploadResult.added ?? 0} • Skipped (Auth):{" "}
                {uploadResult.skippedAuth ?? 0} • Updated Existing:{" "}
                {uploadResult.updatedExisting ?? 0} • Skipped (Dup):{" "}
                {uploadResult.skippedDup ?? 0} • Failed: {uploadResult.failed ?? 0}
              </div>
            )}
          </div>
        )}

        {/* CSV Mapping Dialog */}
        <Dialog
          open={openCsvMappingDialog}
          onClose={handleCancelCsvMapping}
          fullWidth
          maxWidth="lg"
          PaperProps={{ className: "dialog-shell" }}
        >
          <DialogTitle sx={{ p: 0 }}>
            <div className="dialog-header">
              <div className="font-bold">Map CSV Fields</div>
              <div className="ln-dialog-header-pill px-2 py-0.5 text-[0.7rem]">
                {selectedCategoryName || "No Category"}
              </div>
            </div>
          </DialogTitle>

          <DialogContent dividers sx={{ background: "transparent" }}>
            <div className="text-xs text-slate-600">
              Required DB fields are fixed. Pick matching CSV headers from the dropdowns. Auto-matching is prefilled when possible.
            </div>

            <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white/90">
              <table className="w-full min-w-[680px] text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-bold text-slate-700 border-b border-slate-200">
                      DB Field (fixed)
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-slate-700 border-b border-slate-200">
                      CSV Header
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {CONTACT_IMPORT_FIELD_DEFS.map((def) => (
                    <tr key={def.dbField} className="border-b border-slate-100 last:border-0">
                      <td className="px-3 py-2">
                        <div className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs font-semibold text-[#333999]">
                          {def.dbField}
                          {def.required ? <span className="text-red-600">*</span> : null}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={fieldMapping[def.dbField] || ""}
                          onChange={(e) => handleMappingChange(def.dbField, e.target.value)}
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                        >
                          <option value="">-- Not mapped --</option>
                          {csvHeaders.map((header) => (
                            <option key={header} value={header}>
                              {header}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-5">
              <div className="text-xs font-semibold tracking-wide uppercase text-slate-600">
                Example Rows (Mapped Fields Only)
              </div>
              {!mappedFieldDefs.length ? (
                <div className="mt-2 text-xs text-slate-500">
                  No mapped fields yet.
                </div>
              ) : (
                <div className="mt-2 overflow-x-auto rounded-xl border border-slate-200 bg-white/90 max-h-[300px]">
                  <table className="w-full min-w-[680px] text-xs">
                    <thead className="sticky top-0 z-10 bg-gradient-to-r from-slate-50 to-indigo-50">
                      <tr>
                        {mappedFieldDefs.map((def) => (
                          <th key={def.dbField} className="px-3 py-2 text-left border-b border-slate-200">
                            <div className="font-semibold text-[#333999]">{def.dbField}</div>
                            <div className="text-[10px] text-slate-500 font-normal">
                              {fieldMapping[def.dbField]}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {csvData.slice(0, 5).map((row, idx) => (
                        <tr key={idx}>
                          {mappedFieldDefs.map((def) => {
                            const mappedHeader = fieldMapping[def.dbField];
                            return (
                              <td key={`${idx}-${def.dbField}`} className="px-3 py-2 text-slate-700">
                                {row?.[mappedHeader] ?? ""}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </DialogContent>

          <DialogActions sx={{ p: 0 }}>
            <div className="dialog-actions-bar w-full">
              <button
                type="button"
                className="btn-secondary btn-md"
                onClick={handleCancelCsvMapping}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn-primary btn-md ${
                  uploading || !selectedCategoryName || !String(fieldMapping.Email || "").trim()
                    ? "opacity-60 cursor-not-allowed"
                    : ""
                }`}
                onClick={handleUpload}
                disabled={uploading || !selectedCategoryName || !String(fieldMapping.Email || "").trim()}
              >
                {uploading ? "Uploading..." : "Upload Contacts"}
              </button>
            </div>
          </DialogActions>
        </Dialog>

        {/* Add Single Contact Dialog */}
        <Dialog
          open={openAddContactDialog}
          onClose={handleCloseAddContact}
          fullWidth
          maxWidth="sm"
          PaperProps={{ className: "dialog-shell" }}
        >
          <DialogTitle sx={{ p: 0 }}>
            <div className="dialog-header">
              <div className="font-bold">Add Single Contact</div>
              {selectedCategoryName ? (
                <div className="ln-dialog-header-pill px-2 py-0.5 text-[0.7rem]">
                  {selectedCategoryName}
                </div>
              ) : null}
            </div>
          </DialogTitle>

          <DialogContent dividers sx={{ background: "transparent" }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { label: "First Name", key: "First Name", placeholder: "John" },
                { label: "Last Name", key: "Last Name", placeholder: "Doe" },
                { label: "Email*", key: "Email", placeholder: "john@company.com" },
                { label: "Company", key: "Company", placeholder: "Acme Auctions" },
                { label: "Phone", key: "Phone", placeholder: "+1 555 123 4567" },
                { label: "Address", key: "Address", placeholder: "City, State" },
                { label: "Website", key: "Website", placeholder: "https://..." },
              ].map((f) => (
                <div key={f.key} className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-slate-700">
                    {f.label}
                  </label>
                  <input
                    value={singleContact[f.key]}
                    onChange={(e) => updateSingleContact(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    className="w-full rounded-lg border border-slate-300/70 bg-white/90 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                </div>
              ))}
            </div>

            <div className="mt-3 text-xs text-slate-500">
              Tip: You can also upload CSVs with multiple emails per row separated by commas.
            </div>
          </DialogContent>

          <DialogActions sx={{ p: 0 }}>
            <div className="dialog-actions-bar w-full">
              <button
                type="button"
                className="btn-secondary btn-md"
                onClick={handleCloseAddContact}
                disabled={addingContact}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn-primary btn-md ${
                  addingContact ? "opacity-70 cursor-not-allowed" : ""
                }`}
                onClick={handleAddSingleContact}
                disabled={addingContact || !String(singleContact.Email || "").trim()}
              >
                {addingContact ? "Saving..." : "Save Contact"}
              </button>
            </div>
          </DialogActions>
        </Dialog>

        {/* Uploading / Saving modal */}
        <Dialog
          open={uploading || addingContact}
          onClose={() => {}}
          fullWidth
          maxWidth="xs"
          PaperProps={{ className: "dialog-shell" }}
        >
          <DialogTitle sx={{ p: 0 }}>
            <div className="dialog-header">
              <div className="font-bold">
                {addingContact ? "Saving Contact" : "Uploading"}
              </div>
              <div className="ln-dialog-header-pill px-2 py-0.5 text-[0.7rem]">
                Please wait
              </div>
            </div>
          </DialogTitle>

          <DialogContent sx={{ background: "transparent" }}>
            <div className="flex flex-col items-center gap-3 py-5">
              <CircularProgress sx={{ color: primaryColor }} />
              <div className="text-sm font-semibold text-slate-700">
                {addingContact
                  ? "Submitting contact..."
                  : "Uploading contacts..."}
              </div>
              <div className="text-xs text-slate-500 text-center max-w-[280px]">
                Don’t close this window while the upload is in progress.
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <ToastAlert />
      </div>
    </div>
  );
};

export default MarketingContacts;
